import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import {
  safeGetItem,
  safeRemoveItem,
  safeSetItem,
} from '../shared/safeStorage';

// Single source of truth for the storage keys used by the OAuth round-trips
// below — also cleared on logout (see LOGOUT_STORAGE_KEYS in UserContext.tsx).
export const OAUTH_STORAGE_KEYS = [
  'samsaraCode',
  'samsaraScope',
  'samsaraState',
  'motiveCode',
  'referrer',
];

export interface OAuthParams {
  samsara?: {
    code: string;
    scope: string;
    state: string;
  };
  motive?: {
    code: string;
  };
}

export const useOAuthHandler = () => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) return;

    const searchParams = new URLSearchParams(window.location.search);
    const referrer = safeGetItem('referrer');

    // Handle Samsara OAuth
    if (
      referrer === 'samsara' &&
      searchParams.has('code') &&
      searchParams.has('scope') &&
      searchParams.has('state')
    ) {
      const samsaraCode = searchParams.get('code');
      const samsaraScope = searchParams.get('scope');
      const samsaraState = searchParams.get('state');

      safeSetItem('samsaraCode', samsaraCode || '');
      safeSetItem('samsaraScope', samsaraScope || '');
      safeSetItem('samsaraState', samsaraState || '');

      // Clean up URL
      searchParams.delete('code');
      searchParams.delete('scope');
      searchParams.delete('state');
      searchParams.delete('referrer');

      const newUrl =
        window.location.pathname +
        (searchParams.toString() ? `?${searchParams.toString()}` : '');
      window.history.replaceState({}, '', newUrl);
    }

    // Handle Motive OAuth
    if (referrer === 'motive' && searchParams.has('code')) {
      safeSetItem('motiveCode', searchParams.get('code') ?? '');

      // Clean up URL
      searchParams.delete('code');
      searchParams.delete('redirect_uri');
      searchParams.delete('referrer');

      const newUrl =
        window.location.pathname +
        (searchParams.toString() ? `?${searchParams.toString()}` : '');
      window.history.replaceState({}, '', newUrl);
    }
  }, [location, isAuthenticated]);

  const getOAuthParams = (): OAuthParams => {
    const params: OAuthParams = {};

    // Check for Samsara params
    const samsaraCode = safeGetItem('samsaraCode');
    const samsaraScope = safeGetItem('samsaraScope');
    const samsaraState = safeGetItem('samsaraState');
    if (samsaraCode && samsaraScope && samsaraState) {
      params.samsara = {
        code: samsaraCode,
        scope: samsaraScope,
        state: samsaraState,
      };
    }

    // Check for Motive params
    const motiveCode = safeGetItem('motiveCode');
    if (motiveCode) {
      params.motive = {
        code: motiveCode,
      };
    }

    return params;
  };

  const clearOAuthParams = () => {
    OAUTH_STORAGE_KEYS.forEach((key) => safeRemoveItem(key));
  };

  return {
    oauthParams: getOAuthParams(),
    clearOAuthParams,
  };
};
